import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { formatDecimalPlaces, formatPercent } from '@core/util/number';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterAdjustmentVacantTax } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { calculateVacantTax } from '@services/adjustments/vacant-tax';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';

@UntilDestroy()
@Component({
	selector: 'app-tenancy-prepaid',
	templateUrl: './vacant-property-dialog.component.html',
	standalone: false,
})
export class VacantPropertyDialogComponent implements OnInit {
	@Select(AdjustmentsSelector.vacantTaxAdjustment)
	vacantTaxAdjustment$: Observable<MatterAdjustmentVacantTax>;
	@ViewSelectSnapshot(SelectedMatter.matterHST) matterHst: number;
	@ViewSelectSnapshot(SelectedMatter.purchasePrice) price: number;
	@ViewSelectSnapshot(MatterJurisdictionSelectors.getPrimary)
	primaryJurisdiction: Jurisdiction;
	public vacantTaxAdjustment: MatterAdjustmentVacantTax;
	public total: number;
	public hstAmount: number;
	public taxAmount: number;
	public vhtRate: number = formatPercent(0.01);
	public decimalPlaces: number = 4;
	public ICON = ICON;
	public vhtLink: string;
	public jurisdictionName: string;
	public jurisdictionId: string;
	constructor(
		private adjustmentDispatch: AdjustmentsDispatch,
		private matDialogRef: MatDialogRef<VacantPropertyDialogComponent>,
	) {}

	ngOnInit(): void {
		if (this.primaryJurisdiction) {
			this.jurisdictionName = this.primaryJurisdiction.name;
			if (this.primaryJurisdiction.name.length > 30) {
				this.jurisdictionName =
					this.primaryJurisdiction.name.substring(0, 30) + '...';
			}
			this.jurisdictionId = this.primaryJurisdiction.id;
			this.vhtLink = this.primaryJurisdiction.vhtLink;
		}
		this.vacantTaxAdjustment$
			.pipe(untilDestroyed(this))
			.subscribe((vacantTaxAdjustment) => {
				this.vacantTaxAdjustment = cloneDeep(vacantTaxAdjustment);

				if (this.vacantTaxAdjustment?.vht)
					this.vhtRate = +formatDecimalPlaces(
						this.vacantTaxAdjustment?.vht * 100,
						this.decimalPlaces,
					);
				this.changed();
			});
	}

	setPurchasePrice() {
		this.vacantTaxAdjustment.propertyValue = this.price;
		this.changed();
	}
	changed() {
		if (this.vacantTaxAdjustment) {
			const { propertyValue, filingPenalty, interestOnUnpaidTax, applyHst } =
				this.vacantTaxAdjustment;

			this.vhtRate = +formatDecimalPlaces(this.vhtRate, this.decimalPlaces);

			const calculation = calculateVacantTax(
				propertyValue,
				interestOnUnpaidTax,
				filingPenalty,
				applyHst,
				this.matterHst,
				this.vhtRate / 100,
			);

			this.total = -calculation.creditAmount;
			this.hstAmount = calculation.hstAmount;
			this.taxAmount = calculation.unpaidVacantHomeTax;
		}
	}

	submit(form: NgForm) {
		if (form.valid) {
			this.vacantTaxAdjustment.vht = +formatDecimalPlaces(
				this.vhtRate / 100,
				this.decimalPlaces,
			);

			this.adjustmentDispatch.Set({
				...this.vacantTaxAdjustment,
				deleted: false,
			});
			this.close();
		}
	}
	close() {
		this.matDialogRef.close();
	}
}
